/**
 * The public header's language menu on the way-in pages (Claude Design's copy of 13 Oct 2026, 20:33 UTC; the `.wl`
 * button and list of the `.wh` header): the current language's flag and code on a button that opens a two-entry menu,
 * English and Ελληνικά, each a `menuitemradio`. The portal's copy of the public site's `components/LanguageMenu.astro`
 * and `i18n/language-menu.ts`, drawn by React: the button shows the portal's language, the tick follows it, and a choice
 * goes through the language boundary's own switch (`usePortalLanguageSwitch`) — the call `PortalLanguagePicker` makes.
 *
 * The keyboard is the public menu's: Enter, Space, ArrowDown or ArrowUp on the button open it on the current entry;
 * the arrows, Home and End move; Escape closes it and returns to the button (and is not taken by the phone menu as
 * well); Tab closes it; a press outside closes it.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { usePortalLanguageSwitch } from "../../i18n/language-control.js";
import { portalText, usePortalTranslation, type PortalLocale } from "../../i18n/portal.js";

/** A language's own name and code: they stay in their own language, so a person can find theirs. */
const LANGUAGE = [
  { code: "en", name: "English", sub: "EN", short: "EN" },
  { code: "el", name: "Ελληνικά", sub: "EL · Greek", short: "EL" },
] as const satisfies readonly { readonly code: PortalLocale; readonly name: string; readonly sub: string; readonly short: string }[];

function UnionJack(): ReactNode {
  return (
    <svg viewBox="0 0 60 30" width="24" height="16" preserveAspectRatio="xMidYMid slice">
      <rect className="wl-uk-blue" width="60" height="30" />
      <path className="wl-uk-white wl-uk-diagonal" d="M0,0 L60,30 M60,0 L0,30" />
      <path className="wl-uk-red wl-uk-diagonal-red" d="M0,0 L60,30 M60,0 L0,30" />
      <path className="wl-uk-white wl-uk-cross" d="M30,0 v30 M0,15 h60" />
      <path className="wl-uk-red wl-uk-cross-red" d="M30,0 v30 M0,15 h60" />
    </svg>
  );
}

function GreekEnsign(): ReactNode {
  return (
    <svg viewBox="0 0 27 18" width="24" height="16" preserveAspectRatio="xMidYMid slice">
      <rect className="wl-gr-blue" width="27" height="18" />
      <path className="wl-gr-white" d="M0,3h27M0,7h27M0,11h27M0,15h27" />
      <rect className="wl-gr-blue" width="10" height="10" />
      <path className="wl-gr-white" d="M5,0v10M0,5h10" />
    </svg>
  );
}

function LanguageEnsign({ code }: { readonly code: PortalLocale }): ReactNode {
  return <span className="wl-flag" aria-hidden="true">{code === "el" ? <GreekEnsign /> : <UnionJack />}</span>;
}

export function HeaderLanguageMenu({ menuId }: { readonly menuId: string }): ReactNode {
  const control = usePortalLanguageSwitch();
  const { locale } = usePortalTranslation("core");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  /** Set when the list opens, so the current entry takes focus once it is drawn. */
  const focusCurrent = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    if (focusCurrent.current) {
      focusCurrent.current = false;
      listRef.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
    }
    const onClick = (event: MouseEvent): void => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target) !== true) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("click", onClick);
    };
  }, [open]);

  if (control === null) return null;
  const current = LANGUAGE.find((one) => one.code === locale) ?? LANGUAGE[0];

  const show = (): void => {
    focusCurrent.current = true;
    setOpen(true);
  };
  const close = (focusButton: boolean): void => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  };
  const options = (): HTMLElement[] => [...(listRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? [])];
  const move = (step: number): void => {
    const all = options();
    const index = all.indexOf(document.activeElement as HTMLElement);
    all[(index + step + all.length) % all.length]?.focus();
  };
  const onButtonKey = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      show();
    }
  };
  const onListKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Home") {
      event.preventDefault();
      options()[0]?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      options().at(-1)?.focus();
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  return (
    <div className="wl" ref={rootRef}>
      <button
        type="button"
        ref={buttonRef}
        className="wl-button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => {
          if (open) close(false);
          else show();
        }}
        onKeyDown={onButtonKey}
      >
        <LanguageEnsign code={current.code} />
        <span className="wl-sr">{portalText("core", "site_header.language")}</span>
        <span className="wl-code">{current.short}</span>
        <svg className="wl-chevron" stroke="currentColor" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      <div
        className="wl-menu"
        id={menuId}
        ref={listRef}
        role="menu"
        aria-label={portalText("core", "site_header.choose_language")}
        hidden={!open}
        onKeyDown={onListKey}
      >
        <div className="wl-heading" aria-hidden="true">
          {portalText("core", "site_header.language")}
        </div>
        {LANGUAGE.map((one) => (
          <button
            key={one.code}
            type="button"
            className="wl-option"
            role="menuitemradio"
            aria-checked={one.code === locale}
            lang={one.code}
            disabled={control.busy}
            onClick={() => {
              if (one.code !== locale) control.choose(one.code);
              close(true);
            }}
          >
            <LanguageEnsign code={one.code} />
            <span className="wl-names">
              <span className="wl-name">{one.name}</span>
              <span className="wl-sub">{one.sub}</span>
            </span>
            <svg className="wl-tick" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}
